import React, { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { useProfile } from "../../hooks/useProfile";
import { logger } from "../../lib/logger";
import { PageHeaderSkeleton } from "../../components/skeleton/PageHeaderSkeleton";

interface ProtectedHomeProps {
  onboarding: React.ReactNode;
  home: React.ReactNode;
}

/**
 * ProtectedHome - Simple auth guard with onboarding flow
 * 
 * State machine:
 * 1. initializing → show loading
 * 2. unauthenticated → redirect to login
 * 3. authenticated but email not confirmed → redirect to confirm-email
 * 4. authenticated but profile loading → show skeleton
 * 5. authenticated but not onboarded → show onboarding
 * 6. authenticated and onboarded → show home
 * 
 * No localStorage redirects - everything flows through URL params
 */
export const ProtectedHome: React.FC<ProtectedHomeProps> = ({
  onboarding,
  home
}) => {
  const { status, initializing, loadInitialSession, user } = useAuthStore();
  const { data: profile, isLoading: profileLoading } = useProfile();

  useEffect(() => {
    loadInitialSession().catch((err) => {
      logger.error("Error initializing auth", err);
    });
  }, [loadInitialSession]);

  // State 1: Initializing
  if (initializing) {
    return <div className="ob-loading">Checking your session…</div>;
  }

  // State 2: Not authenticated
  if (status !== "authenticated") {
    return <Navigate to="/login" replace />;
  }

  // State 3: Email not confirmed
  if (!user?.email_confirmed_at) {
    return <Navigate to="/confirm-email" replace />;
  }

  // State 4: Profile loading
  if (profileLoading) {
    return (
      <div className="ob-page">
        <div className="ob-page-top-section">
          <PageHeaderSkeleton />
        </div>
      </div>
    );
  }

  // State 5: Needs onboarding
  if (!profile?.has_completed_onboarding) {
    return <>{onboarding}</>;
  }

  // State 6: Ready - show home
  return <>{home}</>;
};

